/**
 * PasskeysCard — the account's passkeys (ADR-024 §10).
 *
 * Lists every passkey the account holds — fias.io's and each Company portal's
 * (each works only on its own site) — and lets the person remove any of them,
 * and add one for fias.io.
 *
 * Adding a passkey adds a way into the account, so it starts with the step-up
 * every sensitive action takes (password, plus the authenticator code when
 * enrolled). The browser's passkey prompt then needs a click of its own — some
 * browsers refuse a prompt that follows a network wait — so the step-up only
 * fetches the options, and "Make the passkey" uses them.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, type PasskeyDto } from '@/lib/auth-api';
import {
  PasskeyCancelled,
  createPasskeyCredential,
  passkeysAvailable,
  type PublicKeyCredentialCreationOptionsJSON,
} from '@/lib/passkeys';
import { queryKeys } from '@/lib/query-keys';
import { stepUpFetch } from '@/lib/step-up-fetch';
import { toast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StepUpDialog, type StepUpCredentials } from '@/auth/components/StepUpDialog';

function siteLabel(p: PasskeyDto): string {
  return p.site.kind === 'fias' ? 'fias.io' : p.site.hostname;
}

function dateLabel(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'unknown' : d.toLocaleDateString();
}

export function PasskeysCard() {
  const queryClient = useQueryClient();
  const supported = passkeysAvailable();
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [pending, setPending] = useState<PublicKeyCredentialCreationOptionsJSON | null>(null);
  const [making, setMaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const list = useQuery({
    queryKey: queryKeys.passkeys.list(),
    queryFn: () => authApi.listPasskeys(),
  });

  const remove = useMutation({
    mutationFn: (credentialId: string) => authApi.removePasskey(credentialId),
    onSuccess: () => {
      setConfirmRemove(null);
      toast({ title: 'Passkey removed' });
      void queryClient.invalidateQueries({ queryKey: queryKeys.passkeys.all });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not remove it.'),
  });

  // Step 1: the step-up; its reply is the options for the browser.
  const confirmStepUp = async (creds: StepUpCredentials) => {
    const { options } = await stepUpFetch<{ options: PublicKeyCredentialCreationOptionsJSON }>(
      '/auth/passkeys/register/options',
      {
        method: 'POST',
        body: {
          currentPassword: creds.currentPassword,
          ...(creds.totpCode ? { totpCode: creds.totpCode } : {}),
        },
      },
    );
    setError(null);
    setPending(options);
  };

  // Step 2: the click the browser's prompt needs.
  const makePasskey = async () => {
    if (!pending) return;
    setMaking(true);
    setError(null);
    try {
      const credential = await createPasskeyCredential(pending);
      await authApi.registerPasskey(credential);
      setPending(null);
      toast({
        title: 'Passkey added',
        description: 'Next time, choose “Sign in with a passkey” on fias.io.',
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.passkeys.all });
    } catch (err) {
      // Its challenge is spent either way: start again from the step-up.
      setPending(null);
      setError(
        err instanceof PasskeyCancelled
          ? 'The passkey was not made. Add it again when you are ready.'
          : err instanceof Error
            ? err.message
            : 'The passkey could not be added.',
      );
    } finally {
      setMaking(false);
    }
  };

  const passkeys = list.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Passkeys</CardTitle>
        <CardDescription>
          Sign in with your fingerprint, face or device PIN instead of a password. A passkey works
          only on the site it was made for.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {list.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : list.isError ? (
          <p className="text-sm text-destructive">Your passkeys could not be loaded.</p>
        ) : passkeys.length === 0 ? (
          <p className="text-sm text-muted-foreground">You have no passkeys yet.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {passkeys.map((p) => (
              <li key={p.credentialId} className="flex items-start justify-between gap-3 p-3">
                <div className="min-w-0 space-y-0.5">
                  <p className="truncate text-sm font-medium">{p.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Signs in on <span className="font-medium">{siteLabel(p)}</span>
                    {p.backedUp ? ' · saved to your password manager' : ''}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Added {dateLabel(p.createdAt)} · last used {dateLabel(p.lastUsedAt)}
                  </p>
                </div>
                {confirmRemove === p.credentialId ? (
                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(p.credentialId)}
                    >
                      Remove
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(null)}>
                      Keep
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    aria-label={`Remove ${p.name}`}
                    onClick={() => setConfirmRemove(p.credentialId)}
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        {!supported ? (
          <p className="text-sm text-muted-foreground">
            This browser cannot make passkeys. Use a current browser on your phone or computer.
          </p>
        ) : pending ? (
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm">
              Confirmed. Now make the passkey — your device will ask for your fingerprint, face or
              PIN.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => void makePasskey()} disabled={making}>
                {making ? 'Waiting for your device…' : 'Make the passkey'}
              </Button>
              <Button variant="ghost" onClick={() => setPending(null)} disabled={making}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setStepUpOpen(true)}>
            Add a passkey for fias.io
          </Button>
        )}

        <StepUpDialog
          open={stepUpOpen}
          onOpenChange={setStepUpOpen}
          title="Add a passkey"
          description="A passkey is a new way into your account, so confirm it is you first."
          confirmLabel="Continue"
          onConfirm={confirmStepUp}
        />
      </CardContent>
    </Card>
  );
}
